import axios, { AxiosInstance, AxiosError, AxiosResponse, InternalAxiosRequestConfig, AxiosHeaders } from "axios";
import { FORBIDDEN_ERROR_MSG, INTERNAL_ERROR_MSG, RESOURCE_ALREADY_EXISTS } from "./constants";
import { msalInstance } from "../utils/msalConfig"; // Adjust the import path as needed

const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "", // Fallback to an empty string for safety
});

/**
 * Handles error messages based on the HTTP status code.
 * @param error - The Axios error object.
 * @throws Error with a user-friendly message.
 */
function handleErrorMessages(error: AxiosError): void {
  if (error.response) {
    switch (error.response.status) {
      case 500:
        throw new Error(INTERNAL_ERROR_MSG);
      case 403:
        throw new Error(FORBIDDEN_ERROR_MSG);
      case 409:
        throw new Error(RESOURCE_ALREADY_EXISTS);
      default:
        throw new Error(typeof error.response.data === 'string' ? error.response.data : "An unexpected error occurred.");
    }
  } else if (error.request) {
    throw new Error("No response received from the server.");
  } else {
    throw new Error(error.message);
  }
}

// Request interceptor
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

    if (!account) {
      console.warn("No MSAL account available for token acquisition.");
      return config;
    }

    let token: string | null = null;

    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: [ process.env.REACT_APP_ENTRA_SSO_SCOPE! ],
        account: account
      });

      token = response.accessToken;
    } catch (error) {
      console.error("Token acquisition failed:", error);
    }

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    handleErrorMessages(error);
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    handleErrorMessages(error);
    return Promise.reject(error);
  }
);

export default axiosInstance;
